import React, { createContext, useContext, useState, useCallback } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  CheckmarkCircle01Icon, 
  AlertCircleIcon, 
  InformationCircleIcon, 
  Cancel01Icon 
} from '@hugeicons/core-free-icons';

const ToastContext = createContext();

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type = 'success', duration = 4000) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type, duration }]);

    setTimeout(() => {
      removeToast(id);
    }, duration);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ addToast, removeToast }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-3 pointer-events-none sm:max-w-md w-full px-4 sm:px-0">
        {toasts.map(toast => (
          <ToastItem key={toast.id} {...toast} onRemove={() => removeToast(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

const ToastItem = ({ message, type, onRemove }) => {
  const styles = {
    success: { bg: 'bg-white dark:bg-dark-surface', border: 'border-soft-green', text: 'text-soft-green', icon: CheckmarkCircle01Icon },
    error: { bg: 'bg-white dark:bg-dark-surface', border: 'border-red-500', text: 'text-red-500', icon: AlertCircleIcon },
    info: { bg: 'bg-white dark:bg-dark-surface', border: 'border-blue-500', text: 'text-blue-500', icon: InformationCircleIcon },
  };

  const style = styles[type] || styles.success;
  const Icon = style.icon;

  return (
    <div className={`pointer-events-auto flex items-start gap-3 p-4 rounded-2xl shadow-float-lg border-l-4 ${style.bg} ${style.border} animate-in fade-in slide-in-from-top-4 duration-300`}>
      <div className={`p-1 shrink-0 ${style.text}`}>
        <HugeiconsIcon icon={Icon} size={20} />
      </div>
      <p className="text-sm font-bold text-charcoal dark:text-white pt-0.5 leading-tight flex-1">{message}</p>
      <button onClick={onRemove} className="text-muted-gray hover:text-charcoal dark:hover:text-white transition-colors">
        <HugeiconsIcon icon={Cancel01Icon} size={16} />
      </button>
    </div>
  );
};

export const useToast = () => useContext(ToastContext);
